// Type definitions for AxisCare conflicts and visits
type AttributeConflict = {
    readonly label: string;
};

type Visit = {
    readonly start_time?: string;
    readonly end_time?: string;
};

type ConflictsObject = {
    readonly attribute_conflicts?: Array<AttributeConflict>;
    readonly overlapping_visits?: Array<unknown>;
    readonly client_block?: boolean;
};

export const ELIGIBLE_CAREGIVERS_PAGE_SIZE_OPTIONS = [25, 50, 75, 100] as const;
export const DEFAULT_ELIGIBLE_CAREGIVERS_PAGE_SIZE = 50;

export function normalizeEligibleCaregiversPageSize(
    value: number | null | undefined,
): number {
    if (
        value !== null &&
        value !== undefined &&
        ELIGIBLE_CAREGIVERS_PAGE_SIZE_OPTIONS.includes(
            value as (typeof ELIGIBLE_CAREGIVERS_PAGE_SIZE_OPTIONS)[number],
        )
    ) {
        return value;
    }

    return DEFAULT_ELIGIBLE_CAREGIVERS_PAGE_SIZE;
}

/**
 * Calculates availability percentage and returns a corresponding Tailwind CSS color class based on thresholds.
 * @param scheduled - Hours scheduled for the week.
 * @param preferred - Preferred weekly hours.
 * @returns An object with the percentage and a color class string.
 */
export function getAvailabilityInfo(
    scheduled?: number,
    preferred?: number,
): { percent: number | null; colorClass: string } {
    if (scheduled === undefined || preferred === undefined || preferred === 0) {
        return { percent: null, colorClass: 'text-gray-500' };
    }

    const percent = Math.round((scheduled / preferred) * 100);

    if (percent > 80) {
        return { percent, colorClass: 'text-green-600' };
    }
    if (percent >= 50) {
        return { percent, colorClass: 'text-yellow-600' };
    }
    return { percent, colorClass: 'text-red-600' };
}

/**
 * Formats distance for display
 * @param distance - Distance in miles
 * @returns Formatted distance string
 */
export function formatDistance(distance?: number | null): string {
    if (distance === undefined || distance === null) {
        return '—';
    }
    return `${distance.toFixed(1)} mi`;
}

/**
 * Formats hours for display with optional percentage
 * @param scheduled - Hours scheduled
 * @param percent - Optional percentage to display
 * @returns Formatted hours string
 */
export function formatHours(
    scheduled?: number,
    percent?: number | null,
): string {
    if (scheduled === undefined || scheduled === null) {
        return '—';
    }

    const hoursStr = `${scheduled}hrs`;
    if (percent !== null && percent !== undefined) {
        return `${hoursStr} (${percent}%)`;
    }
    return hoursStr;
}

/**
 * Checks if two time ranges overlap
 * @param start1 - Start time of first range (HH:MM format)
 * @param end1 - End time of first range (HH:MM format)
 * @param start2 - Start time of second range (HH:MM format)
 * @param end2 - End time of second range (HH:MM format)
 * @returns Whether the time ranges overlap
 */
function timeRangesOverlap(
    start1: string,
    end1: string,
    start2: string,
    end2: string,
): boolean {
    // Convert time strings to minutes since midnight for easier comparison
    const toMinutes = (time: string) => {
        const parts = time.split(':');
        const hours = parseInt(parts[0] || '0', 10);
        const minutes = parseInt(parts[1] || '0', 10);
        return hours * 60 + minutes;
    };

    const start1Min = toMinutes(start1);
    const end1Min = toMinutes(end1);
    const start2Min = toMinutes(start2);
    const end2Min = toMinutes(end2);

    // Check if ranges overlap
    return start1Min < end2Min && end1Min > start2Min;
}

/**
 * Checks if a caregiver has any conflicts
 * @param conflicts - Conflicts object from AxisCare
 * @param visitsToday - Array of visits for today
 * @param shiftStartTime - The shift's start time (HH:MM format)
 * @param shiftEndTime - The shift's end time (HH:MM format)
 * @returns Whether the caregiver has conflicts
 */
export function hasConflicts(
    conflicts?: ConflictsObject,
    visitsToday?: Array<Visit>,
    shiftStartTime?: string,
    shiftEndTime?: string,
): boolean {
    // Check for client block
    if (conflicts?.client_block === true) {
        return true;
    }

    // Check for attribute conflicts
    if (
        conflicts?.attribute_conflicts &&
        conflicts.attribute_conflicts.length > 0
    ) {
        return true;
    }

    // Check for overlapping visits in conflicts object
    if (
        conflicts?.overlapping_visits &&
        conflicts.overlapping_visits.length > 0
    ) {
        return true;
    }

    // Check if visits_today overlap with shift time
    if (
        visitsToday &&
        visitsToday.length > 0 &&
        shiftStartTime &&
        shiftEndTime
    ) {
        return visitsToday.some(
            (visit) =>
                visit.start_time &&
                visit.end_time &&
                timeRangesOverlap(
                    shiftStartTime,
                    shiftEndTime,
                    visit.start_time,
                    visit.end_time,
                ),
        );
    }

    return false;
}

/**
 * Formats time from 24-hour format to 12-hour format with am/pm
 * @param time - Time in HH:MM format
 * @returns Formatted time (e.g., "7:30a", "2:30p", "3p")
 */
function formatTime12Hour(time: string): string {
    const [hoursStr, minutesStr] = time.split(':');
    const hours = parseInt(hoursStr || '0', 10);
    const minutes = parseInt(minutesStr || '0', 10);

    const period = hours >= 12 ? 'p' : 'a';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    // Don't show :00 for on-the-hour times
    if (minutes === 0) {
        return `${displayHours}${period}`;
    }

    return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
}

/**
 * Formats visits for display in the Same-day Visits column
 * @param visits - Array of visits with date, start_time, end_time, and optionally patient name
 * @returns Array of formatted visit objects
 */
export function formatSameDayVisits(
    visits?: Array<{
        visit_date?: string;
        start_time?: string;
        end_time?: string;
        client_name?: string;
    }>,
    timezone?: string,
): Array<{
    date: string;
    timeRange: string;
    clientName: string;
}> | null {
    if (!visits || visits.length === 0) {
        return null;
    }

    return visits.map((visit) => {
        // Format date (e.g., "Jul 24")
        const dateStr = visit.visit_date || '';

        // IMPORTANT: This parses the date into the LOCAL TIMEZONE of the browser
        // The best solution would be to parse the date into UTC time and then
        // include the start/end time in the object, however given how small
        // this feature is and is just displaying the scheduled visits for the
        // caregiver, I think it's fine to default to their local timezone for now
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year!, month! - 1, day!);

        const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            // not actually impactful in current setup
            // timeZone: timezone,
        });

        // Format times
        const startTime = formatTime12Hour(visit.start_time || '00:00');
        const endTime = formatTime12Hour(visit.end_time || '00:00');

        // Get client name
        const clientName = visit.client_name || '';

        return {
            date: formattedDate,
            timeRange: `${startTime} - ${endTime}`,
            clientName,
        };
    });
}

/**
 * Formats conflict information for display
 * @param conflicts - Conflicts object from AxisCare
 * @param visitsToday - Array of visits for today
 * @param shiftStartTime - The shift's start time (HH:MM format)
 * @param shiftEndTime - The shift's end time (HH:MM format)
 * @returns Formatted conflict string
 */

export function formatConflicts(
    conflicts?: ConflictsObject,
    visitsToday?: Array<Visit>,
    shiftStartTime?: string,
    shiftEndTime?: string,
): string {
    const conflictParts: Array<string> = [];

    // Check for client block
    if (conflicts?.client_block === true) {
        conflictParts.push('Caregiver blocked from client');
    }

    // Check for attribute conflicts
    if (
        conflicts?.attribute_conflicts &&
        conflicts.attribute_conflicts.length > 0
    ) {
        const attributes = conflicts.attribute_conflicts
            .map((c) => c.label)
            .join(', ');
        conflictParts.push(`Attribute conflicts: ${attributes}`);
    }

    // Check for overlapping visits in conflicts object
    if (
        conflicts?.overlapping_visits &&
        conflicts.overlapping_visits.length > 0
    ) {
        const visitCount = conflicts.overlapping_visits.length;
        conflictParts.push(
            `${visitCount} overlapping visit${visitCount > 1 ? 's' : ''}`,
        );
    }
    // Check if visits_today overlap with shift time
    else if (
        visitsToday &&
        visitsToday.length > 0 &&
        shiftStartTime &&
        shiftEndTime
    ) {
        const overlappingVisits = visitsToday.filter(
            (visit) =>
                visit.start_time &&
                visit.end_time &&
                timeRangesOverlap(
                    shiftStartTime,
                    shiftEndTime,
                    visit.start_time,
                    visit.end_time,
                ),
        );

        if (overlappingVisits.length > 0) {
            conflictParts.push(
                `${overlappingVisits.length} overlapping visit${overlappingVisits.length > 1 ? 's' : ''}`,
            );
        }
    }

    return conflictParts.length > 0 ? conflictParts.join('; ') : '—';
}
